// components/SucursalesMercadoLibre/SucursalList.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Search,
  Delete,
  Download,
  Refresh,
  PointOfSale,
  Storefront,
  ExpandMore,
  QrCodeScanner,
  Add,
  LocationOn,
  Store,
  ViewList,
  ViewModule,
  Phone,
  Email,
  Schedule,
  Info,
  Edit,  // ← AGREGAR IMPORT DE Edit
} from '@mui/icons-material';
import EditarSucursalModal from './EditarSucursalModal';
import EditarCajaModal from './EditarCajaModal';

const SucursalesList = ({ 
  sucursales, 
  cajas, 
  onEliminarSucursal, 
  onEliminarCaja, 
  onSeleccionarSucursal,
  onActualizarSucursal,
  onActualizarCaja,   
  onRecargar 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grouped');
  const [qrModal, setQrModal] = useState({ open: false, caja: null });
  const [expandedSucursal, setExpandedSucursal] = useState(null);
  const [sucursalesMap, setSucursalesMap] = useState({});
  const [editSucursalModal, setEditSucursalModal] = useState({ open: false, sucursal: null });
  const [editCajaModal, setEditCajaModal] = useState({ open: false, caja: null });

  useEffect(() => {
    const map = {};
    sucursales.forEach(sucursal => {
      map[sucursal.id] = sucursal;
      map[String(sucursal.id)] = sucursal;
      if (sucursal.external_id) {
        map[sucursal.external_id] = sucursal;
      }
    });
    setSucursalesMap(map);
  }, [sucursales]);

  const getSucursalByStoreId = (storeId) => {
    return sucursalesMap[storeId] || sucursalesMap[String(storeId)] || sucursalesMap[Number(storeId)];
  };

  const getSucursalNombre = (storeId) => {
    const sucursal = getSucursalByStoreId(storeId);
    return sucursal?.name || `Sucursal ID: ${storeId}`;
  };

  const getSucursalExternalId = (storeId) => {
    const sucursal = getSucursalByStoreId(storeId);
    return sucursal?.external_id || storeId;
  };

  const sucursalesFiltradas = sucursales.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.external_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

// Reemplaza la función agruparCajasPorSucursal con esta:

const agruparCajasPorSucursal = () => {
  const grupos = new Map();
  
  // Primero, agregar todas las sucursales (incluso sin cajas)
  sucursales.forEach(sucursal => {
    const key = String(sucursal.id);
    if (!grupos.has(key)) {
      grupos.set(key, {
        id: sucursal.id,
        name: sucursal.name,
        external_id: sucursal.external_id,
        sucursalInfo: sucursal,
        cajas: []
      });
    }
  });
  
  // Luego, agregar las cajas a sus respectivas sucursales
  cajas.forEach(caja => {
    const storeId = String(caja.store_id);
    const sucursal = getSucursalByStoreId(storeId);
    
    if (sucursal) {
      const key = String(sucursal.id);
      const grupo = grupos.get(key);
      if (grupo) {
        grupo.cajas.push(caja);
      }
    } else {
      // Si la caja pertenece a una sucursal que no está en la lista
      // (por ejemplo, sucursal no cargada), crearla igual
      const key = storeId;
      if (!grupos.has(key)) {
        grupos.set(key, {
          id: storeId,
          name: `Sucursal ID: ${storeId}`,
          external_id: storeId,
          sucursalInfo: null,
          cajas: [caja]
        });
      } else {
        grupos.get(key).cajas.push(caja);
      }
    }
  });
  
  // Convertir a array y ordenar por nombre
  const resultado = Array.from(grupos.values());
  resultado.sort((a, b) => a.name.localeCompare(b.name));
  
  return resultado;
};

  const gruposCajas = agruparCajasPorSucursal();

const gruposFiltrados = () => {
  // Primero obtener todos los grupos (todas las sucursales)
  const todosLosGrupos = agruparCajasPorSucursal();
  
  if (!searchTerm) return todosLosGrupos;
  
  // Filtrar por término de búsqueda
  return todosLosGrupos.filter(grupo => 
    grupo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    grupo.external_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    grupo.cajas.some(c => 
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.external_id?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  ).map(grupo => ({
    ...grupo,
    cajas: grupo.cajas.filter(c =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.external_id?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }));
};

  const handleAccordionChange = (sucursalId) => (event, isExpanded) => {
    setExpandedSucursal(isExpanded ? sucursalId : null);
  };

  const totalSucursales = sucursales.length;
  const totalCajas = cajas.length;

  // Renderizar tarjeta de información de sucursal
  const renderSucursalInfo = (sucursalInfo) => {
    if (!sucursalInfo) return null;
    
    const location = sucursalInfo.location || {};
    const direccion = location.address_line || 
      `${location.street_name || ''} ${location.street_number || ''}`.trim() ||
      `${location.city_name || ''}, ${location.state_name || ''}`;
    
    return (
      <Box sx={{ mt: 1, mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Info fontSize="small" /> Información de la sucursal
        </Typography>
        <Grid container spacing={1}>
          {direccion && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn sx={{ color: '#666', fontSize: 14 }} />
                <Typography variant="body2" color="text.secondary">
                  {direccion}
                </Typography>
              </Box>
            </Grid>
          )}
          {location.city_name && location.state_name && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Store sx={{ color: '#666', fontSize: 14 }} />
                <Typography variant="body2" color="text.secondary">
                  {location.city_name}, {location.state_name}
                </Typography>
              </Box>
            </Grid>
          )}
          {location.reference && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info sx={{ color: '#666', fontSize: 14 }} />
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  Ref: {location.reference}
                </Typography>
              </Box>
            </Grid>
          )}
          {(location.latitude || location.longitude) && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn sx={{ color: '#666', fontSize: 14 }} />
                <Typography variant="body2" color="text.secondary">
                  📍 {location.latitude?.toFixed(6)}, {location.longitude?.toFixed(6)}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      {/* Header con estadísticas y búsqueda */}
      <Paper elevation={0} sx={{ p: 2, mb: 1, bgcolor: '#f8f9fa', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Store sx={{ color: '#009ee3' }} />
              <Typography variant="body2" color="text.secondary">
                {totalSucursales} sucursal{totalSucursales !== 1 ? 'es' : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PointOfSale sx={{ color: '#009ee3' }} />
              <Typography variant="body2" color="text.secondary">
                {totalCajas} caja{totalCajas !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              placeholder="Buscar sucursal o caja..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ color: '#999' }} /></InputAdornment>,
              }}
              sx={{ width: 300 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Vista agrupada por sucursal">
                <IconButton 
                  size="small" 
                  onClick={() => setViewMode('grouped')}
                  sx={{ bgcolor: viewMode === 'grouped' ? '#009ee3' : '#f0f0f0', color: viewMode === 'grouped' ? 'white' : '#666' }}
                >
                  <ViewModule fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Vista de lista">
                <IconButton 
                  size="small" 
                  onClick={() => setViewMode('list')}
                  sx={{ bgcolor: viewMode === 'list' ? '#009ee3' : '#f0f0f0', color: viewMode === 'list' ? 'white' : '#666' }}
                >
                  <ViewList fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Tooltip title="Recargar datos">
              <IconButton onClick={onRecargar} size="small" sx={{ bgcolor: '#f0f0f0' }}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {viewMode === 'list' ? (
        // VISTA DE LISTA
        <Box>
          {sucursalesFiltradas.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Store sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography color="text.secondary" gutterBottom>
                No hay sucursales creadas
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<Add />}
                sx={{ bgcolor: '#009ee3', '&:hover': { bgcolor: '#0080b3' } }}
                onClick={() => window.location.hash = '#2'}
              >
                Crear primera sucursal
              </Button>
            </Paper>
          ) : (
            <Accordion defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">📋 Lista de Sucursales</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell><strong>ID MP</strong></TableCell>
                        <TableCell><strong>Nombre</strong></TableCell>
                        <TableCell><strong>ID Externo</strong></TableCell>
                        <TableCell><strong>Ubicación</strong></TableCell>
                        <TableCell><strong>Cajas</strong></TableCell>
                        <TableCell><strong>Acciones</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sucursalesFiltradas.map((sucursal) => {
                        const cajasSucursal = cajas.filter(c => String(c.store_id) === String(sucursal.id));
                        const direccion = sucursal.location?.address_line || 
                          `${sucursal.location?.street_name || ''} ${sucursal.location?.street_number || ''}`;
                        return (
                          <TableRow key={sucursal.id} hover>
                            <TableCell>
                              <Chip label={sucursal.id} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Store sx={{ color: '#009ee3', fontSize: 18 }} />
                                <Typography fontWeight="500">{sucursal.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <code style={{ fontSize: 12 }}>{sucursal.external_id}</code>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={direccion}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <LocationOn sx={{ color: '#999', fontSize: 14 }} />
                                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                    {direccion || 'Sin dirección'}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Badge badgeContent={cajasSucursal.length} color="primary" />
                            </TableCell>
                            <TableCell>
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => onSeleccionarSucursal(sucursal)}
                                title="Agregar caja"
                              >
                                <Add />
                              </IconButton>
                              {/* <IconButton 
                                size="small" 
                                color="warning"
                                onClick={() => setEditSucursalModal({ open: true, sucursal })}
                                title="Editar sucursal"
                              >
                                <Edit fontSize="small" />
                              </IconButton> */}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {cajas.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">🏪 Todas las Cajas</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {cajas.map((caja) => (
                    <Grid item xs={12} md={6} lg={4} key={caja.id}>
                      <Card sx={{ borderRadius: 2, height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <PointOfSale sx={{ color: '#009ee3' }} />
                            <Typography variant="subtitle1" fontWeight="600">{caja.name}</Typography>
                            <Chip label={caja.status || 'activa'} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                          </Box>
                          <Typography variant="caption" color="text.secondary">Sucursal</Typography>
                          <Typography variant="body2">{getSucursalNombre(caja.store_id)}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>ID Externo</Typography>
                          <Typography variant="body2" fontFamily="monospace">{caja.external_id}</Typography>
                        </CardContent>
                        <CardActions>
                          <IconButton 
                            size="small" 
                            color="warning"
                            onClick={() => setEditCajaModal({ open: true, caja })}
                            title="Editar caja"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          {caja.qr?.image && (
                            <IconButton size="small" color="primary" onClick={() => setQrModal({ open: true, caja })}>
                              <QrCodeScanner />
                            </IconButton>
                          )}
                          <IconButton size="small" color="error" onClick={() => onEliminarCaja(caja.id)}>
                            <Delete />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      ) : (
        // VISTA AGRUPADA - Sucursales con info y sus cajas
        <Box>
          {gruposFiltrados().length === 0 && sucursalesFiltradas.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Store sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No hay sucursales ni cajas creadas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {searchTerm ? 'No se encontraron resultados para tu búsqueda' : 'Comienza creando tu primera sucursal'}
              </Typography>
              {!searchTerm && (
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  sx={{ bgcolor: '#009ee3', '&:hover': { bgcolor: '#0080b3' } }}
                  onClick={() => window.location.hash = '#2'}
                >
                  Crear primera sucursal
                </Button>
              )}
            </Box>
          ) : (
            gruposFiltrados().map((grupo) => (
              <Accordion 
                key={grupo.id}
                expanded={expandedSucursal === grupo.id}
                onChange={handleAccordionChange(grupo.id)}
                defaultExpanded={true}
                sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
              >
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: '#fafafa', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                    <Storefront sx={{ color: '#009ee3' }} />
                    <Typography variant="subtitle1" fontWeight="600">{grupo.name}</Typography>
                    <Badge badgeContent={grupo.cajas.length} color="primary" sx={{ '& .MuiBadge-badge': { bgcolor: '#009ee3' } }} />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      ID: {grupo.id}
                    </Typography>
                    {/* <IconButton 
                      size="small" 
                      color="warning"
                      onClick={() => setEditSucursalModal({ open: true, sucursal: grupo.sucursalInfo })}
                      title="Editar sucursal"
                    >
                      <Edit fontSize="small" />
                    </IconButton> */}
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onSeleccionarSucursal({ id: grupo.id, name: grupo.name, external_id: grupo.external_id }); }} title="Agregar caja">
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails sx={{ p: 2 }}>
                  {/* Información adicional de la sucursal */}
                  {renderSucursalInfo(grupo.sucursalInfo)}
                  
                  {grupo.cajas.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">No hay cajas en esta sucursal</Typography>
                      <Button size="small" startIcon={<Add />} onClick={() => onSeleccionarSucursal({ id: grupo.id, name: grupo.name, external_id: grupo.external_id })} sx={{ mt: 1 }}>
                        Agregar caja
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PointOfSale sx={{ fontSize: 18 }} /> Cajas disponibles
                      </Typography>
                      <Grid container spacing={2}>
                        {grupo.cajas.map((caja) => (
                          <Grid item xs={12} md={6} lg={4} key={caja.id}>
                            <Card sx={{ borderRadius: 2, height: '100%' }}>
                              <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                  <PointOfSale sx={{ color: '#009ee3' }} />
                                  <Typography variant="subtitle1" fontWeight="600">{caja.name}</Typography>
                                  <Chip label={caja.status || 'activa'} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                                </Box>
                                <Typography variant="caption" color="text.secondary">ID Externo</Typography>
                                <Typography variant="body2" fontFamily="monospace">{caja.external_id}</Typography>
                              </CardContent>
                              <CardActions sx={{ justifyContent: 'flex-end' }}>
                                <IconButton 
                                  size="small" 
                                  color="warning"
                                  onClick={() => setEditCajaModal({ open: true, caja })}
                                  title="Editar caja"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                {/* <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => onEliminarCaja(caja.id)}
                                  title="Eliminar caja"
                                >
                                  <Delete fontSize="small" />
                                </IconButton> */}
                              </CardActions>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      )}

      {/* Modal del QR */}
      <Dialog open={qrModal.open} onClose={() => setQrModal({ open: false, caja: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#009ee3', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeScanner />
          Código QR - {qrModal.caja?.name}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Paper elevation={3} sx={{ p: 3, display: 'inline-block', borderRadius: 3 }}>
            <img src={qrModal.caja?.qr?.image} alt="Código QR" style={{ width: '250px', height: '250px', objectFit: 'contain' }} />
          </Paper>
          <Typography variant="body1" sx={{ mt: 3, mb: 1 }}>📱 Escanea este código QR con Mercado Pago</Typography>
          <Typography variant="body2" color="text.secondary">El cliente podrá pagar escaneando este código desde la app</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setQrModal({ open: false, caja: null })} variant="outlined">Cerrar</Button>
          <Button component="a" href={qrModal.caja?.qr?.image} download={`qr_${qrModal.caja?.external_id}.png`} variant="contained" sx={{ bgcolor: '#009ee3' }}>
            <Download sx={{ mr: 1 }} /> Descargar QR
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modales de edición */}
      <EditarSucursalModal
  open={editSucursalModal.open}
  sucursal={editSucursalModal.sucursal}
  onClose={async () => {
    setEditSucursalModal({ open: false, sucursal: null });
    await onRecargar();  // ← usar await
  }}
  onActualizar={async (id, data) => {
    await onActualizarSucursal(id, data);
    await onRecargar();  // ← recargar después de actualizar
  }}
/>

<EditarCajaModal
  open={editCajaModal.open}
  caja={editCajaModal.caja}
  sucursales={sucursales}
  onClose={async () => {
    setEditCajaModal({ open: false, caja: null });
    await onRecargar();
  }}
  onActualizar={async (id, updateDataMP, updateDataLocal) => {
    await onActualizarCaja(id, updateDataMP, updateDataLocal);
    await onRecargar();
  }}
/>
    </Box>
  );
};

export default SucursalesList;